import type { DistributedOmit } from 'type-fest'
import { captureException } from '@sentry/browser'
import { MessageToEmbed, MessageToIframe } from '../../shared/types'
import { runSlideUpAnimation, setupResizeAnimation } from './animations'

declare global {
  interface Window {
    ChiliChat?: {
      config: {
        chatUrl: string
        triggers: {
          elementId: string
          journeyId: string
        }[]
        playbookId: string
        tenantId: string
        workspaceId: string
        zIndex: number
      }
    }
  }
}

window.ChiliChat = {
  config: {
    chatUrl: window.ChiliChat?.config.chatUrl
      ? window.ChiliChat.config.chatUrl
      : (import.meta.env.VITE_CHILICHAT_URL ?? `${window.location.origin}/chat/widget/`),
    workspaceId: window.ChiliChat?.config.workspaceId as string,
    tenantId: window.ChiliChat?.config.tenantId as string,
    playbookId: window.ChiliChat?.config.playbookId as string,
    zIndex: window.ChiliChat?.config.zIndex as number,
    triggers: window.ChiliChat?.config.triggers as {
      elementId: string
      journeyId: string
    }[],
  },
}

export const iframeUrl = window.ChiliChat.config.chatUrl
export const iframeOrigin = new URL(iframeUrl).origin

export const sendMessageToIframe = (
  message: DistributedOmit<MessageToIframe, 'source'>,
  iframe: HTMLIFrameElement
) => {
  const composedMessage: MessageToIframe = { ...message, source: 'chili-chat-embed' }
  iframe.contentWindow?.postMessage(composedMessage, iframeOrigin)
}

interface HandleMessageContext {
  iframe: HTMLIFrameElement
  unsubscribeScrollPercentage: () => void
  unsubscribeUserTimeOnPage: () => void
}

let previousTriggersClickHandler: ((event: MouseEvent) => void) | null = null

export const handleMessage = async (message: MessageToEmbed, context: HandleMessageContext) => {
  switch (message.type) {
    case 'WIDGET_STATE_CHANGE': {
      const { iframe } = context
      iframe.className = message.value
      break
    }
    case 'SET_SIZE': {
      const { height, width } = message.value
      const { iframe } = context
      const oldHeight = iframe.style.height

      iframe.style.height = height ?? iframe.style.height
      iframe.style.width = width ?? iframe.style.width

      // if it's not resizing, slide up
      if (oldHeight === '0px') {
        await runSlideUpAnimation(iframe)
        setupResizeAnimation(iframe)
      }

      break
    }

    case 'SET_TRIGGERS': {
      const { iframe } = context
      // First programatic triggers, then remote configuration ones so that on preview page we don't block user from defining their own trigger on the element
      const triggers = [...(window.ChiliChat?.config.triggers ?? []), ...message.value].map(
        trigger => ({
          ...trigger,
          selector: `#${trigger.elementId}`,
        })
      )

      const clickHandler = (event: MouseEvent) => {
        const target = event.target instanceof Element ? event.target : null
        const triggerSelector = triggers.map(trigger => trigger.selector).join(', ')
        if (triggerSelector === '') {
          return
        }
        const closestTrigger = target?.closest(triggerSelector)
        const journeyId = triggers.find(trigger =>
          closestTrigger?.matches(trigger.selector)
        )?.journeyId
        if (journeyId) {
          sendMessageToIframe({ type: 'TRIGGER_CHAT', value: { journeyId } }, iframe)
        }
      }

      if (previousTriggersClickHandler) {
        window.removeEventListener('click', previousTriggersClickHandler, { capture: true })
      }
      previousTriggersClickHandler = clickHandler
      window.addEventListener('click', clickHandler, { capture: true })

      break
    }

    case 'APPEARANCE_SETTINGS_CHANGE': {
      const { iframe } = context
      const { launcherPosition, bottomSpacing, sideSpacing } = message.value
      iframe.style.bottom = `${bottomSpacing}px`

      if (launcherPosition === 'left') {
        iframe.style.left = `${sideSpacing}px`
        iframe.style.right = 'unset'
      } else {
        iframe.style.right = `${sideSpacing}px`
        iframe.style.left = 'unset'
      }
      break
    }
    case 'STOP_LISTENING_FOR_LOCATION_DATA': {
      context.unsubscribeScrollPercentage()
      context.unsubscribeUserTimeOnPage()
      break
    }
    case 'ERROR': {
      captureException(message.value)
      break
    }
    default: {
      console.error(`Unexpected message from iframe: ${JSON.stringify(message)}`)
    }
  }
}
